
import React from 'react';
import { motion } from 'framer-motion';
import { Check, ChevronDown, Calendar, MessageCircle, Shield, XCircle, AlertCircle, ArrowRight, Layout, Zap } from 'lucide-react';
import { Language, View } from '../types';

interface ImpactPageProps {
  lang: Language;
  setView: (view: View) => void;
  onBack: () => void;
}

const ImpactPage: React.FC<ImpactPageProps> = ({ lang, setView, onBack }) => {
  
  const gradientTextClasses = "bg-gradient-to-r from-primary via-teal-400 to-emerald-500 bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]";
  const gradientBgClasses = "bg-gradient-to-r from-primary via-teal-400 to-emerald-500 animate-gradient bg-[length:200%_auto]";

  const SectionHeader = ({ title, subtitle, textColor = "text-brandDark" }: { title: string, subtitle?: string, textColor?: string }) => (
    <div className="mb-8 md:mb-10">
      <h2 className={`text-2xl md:text-3xl font-black uppercase leading-tight mb-3 ${textColor}`}>
        {title}
      </h2>
      {subtitle && <p className="text-gray-500 font-medium text-base md:text-lg">{subtitle}</p>}
    </div>
  );

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 bg-white selection:bg-teal-500/20">
      <div className="max-w-4xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <h1 className="hero-heading font-black text-brandDark uppercase leading-none mb-6">
            {lang === 'en' ? <>Redesigning<br/>The <span className={`${gradientTextClasses} italic pr-2 inline-block`}>Inner System</span></> : <>بازطراحی<br/><span className={`${gradientTextClasses} italic pr-2 inline-block`}>سیستم درونی</span> زندگی</>}
          </h1>
        </motion.div>

        {/* 1. The Problem & Introduction */}
        <section className="mb-20">
          <div className="prose prose-lg text-gray-500 font-medium leading-relaxed space-y-6">
            {lang === 'en' ? (
              <>
                <p>
                  There are areas of our lives we wish were different. A healthier body, more meaningful work, deeper relationships, financial freedom, courage to express ourselves, peace of mind...
                </p>
                <p>
                  We start many times, get motivated, make some progress... and then let go again. We conclude "I have no discipline," "I'm not smart," "I'm weak-willed," "I'm unmotivated," or "I have no guts." Then we blame ourselves, getting angry and hopeless.
                </p>
                <p className="font-bold text-brandDark text-xl">
                  But the problem is usually not a "character weakness." This is where coaching comes in.
                </p>
                <p>
                  Coaching helps us see the hidden reasons behind these inner patterns and understand how to change them.
                </p>
                <div className="p-6 bg-gray-50 border-l-4 border-teal-500 rounded-r-lg mt-8">
                  <p className="text-brandDark font-bold italic text-lg leading-relaxed">
                    "Coaching is a structured partnership based on becoming aware of our inner system and redesigning it to build the life we deeply want."
                  </p>
                </div>
                <div className="mt-8">
                  <a 
                     href="https://calendar.app.google/NbuyxdkKnzVU9gj17" 
                     target="_blank"
                     rel="noopener noreferrer"
                     className={`inline-flex items-center gap-2 ${gradientTextClasses} font-black uppercase tracking-widest border-b-2 border-teal-500 pb-1 hover:opacity-80 transition-opacity`}
                  >
                    Request Discovery Session <ArrowRight className="w-4 h-4 text-teal-500" />
                  </a>
                </div>
              </>
            ) : (
              <>
                <p>
                  جاهایی از زندگیمون هست که دوست داریم متفاوت باشه. بدن سالم‌تر، کار معنادارتر، رابطه‌ی عمیق‌تر، آزادی مالی، جرأت بیان خودمون، آرامش ذهنی و...
                </p>
                <p>
                  بارها شروع می‌کنیم، انگیزه می‌گیریم، مقداری پیشرفت می‌کنیم… و دوباره رها می‌کنیم.
                  به این نتیجه می‌رسیم که «دیسیپلین ندارم»، «باهوش نیستم»، «بی‌اراده‌ام»، «بی‌انگیزه‌ام»، «جرأت ندارم» و...
                  بعد هم خودمون رو سرزنش می‌کنیم، عصبانی و ناامید می‌شیم.
                </p>
                <p className="font-bold text-brandDark text-xl">
                  اما معمولاً مشکل «ضعف شخصیت» نیست. و اینجاست که کوچینگ وارد می‌شه.
                </p>
                <p>
                  کوچینگ به ما کمک می‌کنه که دلیل‌های مخفی پشت این الگوهای درونیمون رو ببینیم و بفهمیم چطور می‌تونیم تغییرش بدیم.
                </p>
                <div className="p-6 bg-gray-50 border-r-4 border-teal-500 rounded-l-lg mt-8">
                  <p className="text-brandDark font-bold italic text-lg leading-relaxed">
                    «کوچینگ یک شراکت ساختاریافته‌ست مبتنی بر آگاه شدن به سیستم درونی‌مون و بازطراحی اون برای ساخت زندگی‌ای که عمیقن می‌خوایم.»
                  </p>
                </div>
                <div className="mt-8">
                  <a 
                     href="https://calendar.app.google/NbuyxdkKnzVU9gj17" 
                     target="_blank"
                     rel="noopener noreferrer"
                     className={`inline-flex items-center gap-2 ${gradientTextClasses} font-black uppercase tracking-widest border-b-2 border-teal-500 pb-1 hover:opacity-80 transition-opacity`}
                  >
                    درخواست جلسه‌ی اکتشافی <ArrowRight className="w-4 h-4 text-teal-500" />
                  </a>
                </div>
              </>
            )}
          </div>
        </section>

        <div className="w-full h-px bg-gray-100 mb-20"></div>


        {/* 3. A Simple Example */}
        <section className="mb-20">
          <SectionHeader title={lang === 'en' ? "A Simple Example" : "یک مثال ساده"} />
          <div className="bg-gray-50 p-8 md:p-10 rounded-sm border border-gray-100 space-y-6">
             {lang === 'en' ? (
                <>
                  <p className="text-gray-600 font-medium text-lg">
                    Imagine you want to publish your project, but you scroll through social media for hours.
                  </p>
                  <div className="grid md:grid-cols-2 gap-8 my-6">
                    <div className="bg-white p-6 rounded shadow-sm">
                      <h4 className="font-bold text-gray-500 uppercase text-xs tracking-widest mb-2">Surface Level</h4>
                      <p className="font-black text-brandDark text-lg">"I'm lazy or procrastinating."</p>
                    </div>
                    <div className="bg-white p-6 rounded shadow-sm border-2 border-teal-500/20">
                      <h4 className={`${gradientTextClasses} font-bold uppercase text-xs tracking-widest mb-2`}>Deeper Level</h4>
                      <p className="font-black text-brandDark text-lg">A part of you is protecting you from shame, judgment, failure, or pressure.</p>
                    </div>
                  </div>
                  <p className="text-gray-500 leading-relaxed">
                    Here you realize your brain has subconscious goals that conflict with your conscious goals.
                  </p>
                  <p className="text-gray-500 leading-relaxed">
                    <span className="font-bold text-brandDark">And here the game changes completely.</span> The solution can be something else entirely:
                    Instead of force, artificial discipline, and self-blame (which you've tried many times), you understand you need to protect yourself from the damage of blame. Or change your view on others' feedback. Or check if you even need to defend yourself anymore or if your brain is responding to a past memory! Maybe you just need more emotional support.
                  </p>
                  <p className="font-bold text-brandDark italic">
                    Coaching is where these layers are seen, and new solutions are discovered. Where inner wars turn into empathy and conscious choice.
                  </p>
                </>
             ) : (
                <>
                  <p className="text-gray-600 font-medium text-lg">
                    فرض کن می‌خوای پروژه‌ات را منتشر کنی، اما ساعت‌ها در شبکه‌های اجتماعی اسکرول می‌کنی.
                  </p>
                  <div className="grid md:grid-cols-2 gap-8 my-6">
                    <div className="bg-white p-6 rounded shadow-sm">
                      <h4 className="font-bold text-gray-500 uppercase text-xs tracking-widest mb-2">سطح ظاهری</h4>
                      <p className="font-black text-brandDark text-lg">«تنبلم یا اهمال‌کاری می‌کنم.»</p>
                    </div>
                    <div className="bg-white p-6 rounded shadow-sm border-2 border-teal-500/20">
                      <h4 className={`${gradientTextClasses} font-bold uppercase text-xs tracking-widest mb-2`}>لایه عمیق‌تر</h4>
                      <p className="font-black text-brandDark text-lg">شاید بخشی از تو داره از شرم، قضاوت، شکست یا فشار محافظت می‌کنه.</p>
                    </div>
                  </div>
                  <p className="text-gray-500 leading-relaxed">
                    اینجاست که متوجه می‌شی مغز تو اهدافِ ناخودآگاهی داره که با اهداف آگاهانه‌ت در تضادن.
                  </p>
                  <p className="text-gray-500 leading-relaxed">
                    <span className="font-bold text-brandDark">و اینجا بازی کاملن عوض می‌شه.</span> در این صورت راه حل کاملن چیز دیگه‌ای می‌تونه باشه:
                    به جای زور، دیسیپلین مصنوعی و خودسرزنشی (که بارها امتحان کردی و جواب نداده)، می‌فهمی باید جلوی آسیب دیدن از سرزنش رو بگیری. یا دیدگاه‌ت رو به بازخوردهای دیگران عوض کنی. یا بررسی کنی که آیا اصلن هنوزم لازمه از خودت دفاع کنی یا مغز تو داره به خاطره‌ای در گذشته پاسخ می‌ده! شاید فقط به حمایت عاطفی بیشتر احتیاج داری.
                  </p>
                  <p className="font-bold text-brandDark italic">
                    کوچینگ جاییه که این لایه‌ها دیده می‌شن و راه حل‌های جدید کشف می‌شه. جاییه که جنگ‌های درونیمون به همدلی و انتخاب آگاهانه تبدیل می‌شه.
                  </p>
                </>
             )}
          </div>
        </section>

        {/* 3. Results */}
        <section className="mb-20">
          <SectionHeader title={lang === 'en' ? "What Results Do We Experience?" : "چه نتایجی از کوچینگ تجربه می‌کنیم؟"} />
          
          <div className="space-y-6">
            {[
              {
                title: { en: "Radical Clarity", fa: "وضوح رادیکال" },
                desc: { 
                  en: "Understanding what you truly want, not what you think you 'should' want. Maybe you think you want freedom, but deeply prioritize safety. As long as these aren't aligned, we want one thing but do another. Or maybe you need to be seen and validated. Sometimes real change starts with changing the goal, not more force.", 
                  fa: "بفهمی واقعاً چی می‌خوای. نه چیزی که فکر می‌کنی «باید» بخوای یا چیزی که جامعه و خانواده فکر می‌کنه. مثلاً شاید فکر می‌کنی آزادی و سفر دور دنیا هدف‌ته، اما در لایه‌ی عمیق‌تر امنیت برات اولویته. تا وقتی که این دو تا همسو نباشن یه چیزی می‌خوایم اما یه کار دیگه می‌کنیم. یا شاید نیازت دیده‌شدن و تأیید اجتماعیه. گاهی تغییر واقعی از تغییر هدف شروع می‌شه، نه از زور بیشتر." 
                }
              },
              {
                title: { en: "Self-Awareness", fa: "خودآگاهی" },
                desc: { 
                  en: "Observing your inner system and behavioral patterns. Why perfectionism? Why delaying decisions? Every pattern or system in us was built for real, important reasons. Changing them without understanding and respecting this reality is usually unsustainable and leads to more self-blame and pressure.", 
                  fa: "سیستم درونی خودت رو مشاهده کنی و الگوهای رفتاری‌ات را ببینی؛ چرا کمال‌گرایی می‌کنی؟ چرا در روابط یک الگو تکرار میشه؟ چرا تصمیم‌هات رو عقب می‌اندازی؟ هر الگو یا سیستمی در درون ما به دلایل واقعی و مهمی ساخته شده. تغییر اون‌ها بدون فهم و احترام به این واقعیت معمولاً پایدار نیست و به خودسرزنشی و فشار بیشتر منجر می‌شه." 
                }
              },
              {
                title: { en: "Action Without Force", fa: "اقدام بدون زور" },
                desc: { 
                  en: "Moving from 'I have to' to 'I want to'. Life doesn't change with insight alone. But action without insight is often exhausting. Coaching holds both together.", 
                  fa: "حرکت از «باید» به «می‌خواهم». زندگی با بینش تنها تغییر نمی‌کنه. اما اقدام بدون بینش هم اغلب فرسایشی می‌شه. کوچینگ این دو رو کنار هم نگه می‌داره." 
                }
              },
              {
                title: { en: "Companionship & Accountability", fa: "همراهی و پاسخگویی" },
                desc: { 
                  en: "A coach as a companion on the journey helps you stay committed, not stop, and is by your side during hard choices so you actually live what you know.", 
                  fa: "کوچ به عنوان یک همراه در طول مسیر کمکت می‌کنه که متعهد بمونی و متوقف نشی و لحظه‌های انتخاب سخت کنارته تا آونچه می‌دونی رو واقعاً زندگی کنی." 
                }
              }
            ].map((item, i) => (
              <div key={i} className="flex gap-5 items-start p-6 border border-gray-100 rounded-sm hover:shadow-md transition-all">
                <div className="mt-1 bg-teal-50 text-teal-500 rounded-full p-1.5 shrink-0">
                   <Check className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-black text-brandDark text-lg mb-2 uppercase">{item.title[lang]}</h3>
                  <p className="text-gray-500 leading-relaxed font-medium">{item.desc[lang]}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 4. What Coaching Is Not */}
        <section className="mb-20">
          <SectionHeader title={lang === 'en' ? "What Coaching Is Not" : "کوچینگ چه چیزی نیست؟"} />
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 bg-gray-50 rounded-sm">
               <div className="mb-4 text-red-400"><XCircle className="w-8 h-8" /></div>
               <h4 className="font-bold text-brandDark mb-2 uppercase">{lang === 'en' ? "Not Therapy" : "تراپی نیست"}</h4>
               <p className="text-gray-500 font-medium text-sm leading-relaxed">{lang === 'en' ? "Therapy works more with past wounds; coaching focuses on creating the future. For active trauma/PTSD, therapy is the starting point." : "تراپی بیشتر با زخم‌های گذشته کار می‌کنه؛ کوچینگ روی خلق آینده تمرکز داره. برای تروما یا PTSD فعال، تراپی نقطه‌ی شروعه."}</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-sm">
               <div className="mb-4 text-red-400"><XCircle className="w-8 h-8" /></div>
               <h4 className="font-bold text-brandDark mb-2 uppercase">{lang === 'en' ? "Not Consulting" : "مشاوره نیست"}</h4>
               <p className="text-gray-500 font-medium text-sm leading-relaxed">{lang === 'en' ? "I don't say what's right or wrong. The premise is you are the expert of your life. Coaching helps you access that wisdom." : "من نمی‌گم چی درست یا غلطه. فرض پایه اینه که تو بهترین متخصص زندگی خودتی. کوچینگ کمک می‌کنه به این دانایی دسترسی پیدا کنی."}</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-sm">
               <div className="mb-4 text-red-400"><XCircle className="w-8 h-8" /></div>
               <h4 className="font-bold text-brandDark mb-2 uppercase">{lang === 'en' ? "Not Magic" : "معجزه نمی‌کنه"}</h4>
               <p className="text-gray-500 font-medium text-sm leading-relaxed">{lang === 'en' ? "Transformation doesn't happen without your participation. It's joint work requiring your real action." : "تحول بدون مشارکت تو اتفاق نمی‌افته. این یه کار مشترکه و نیاز به اقدام واقعی تو داره."}</p>
            </div>
          </div>
        </section>

        {/* 5. My Method */}
        <section className="mb-20">
          <div className="bg-brandDark text-white p-8 md:p-12 rounded-sm relative overflow-hidden">
             <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 blur-[80px] rounded-full" />
             <div className="relative z-10">
               <SectionHeader title={lang === 'en' ? "My Specific Approach" : "شیوه‌ی ویژه‌ی من"} textColor="text-white" />
               <h3 className="text-xl md:text-2xl font-medium leading-relaxed mb-6 text-gray-200">
                 {lang === 'en' 
                   ? "My work is grounded in modern psychology frameworks: Internal Family Systems (IFS) and Nonviolent Communication (NVC). They help us recognize our inner system and build deeper connections with ourselves and others."
                   : "کار من بر پایه چارچوب‌های روانشناسی مدرن است: سیستم‌های خانواده درونی (IFS) و ارتباط بدون خشونت (NVC). که بهمون کمک می‌کنن سیستم درونیمون رو بشناسیم و با خودمون و اطرافیانمون ارتباط عمیق‌تری بسازیم."}
               </h3>
               <div className="p-6 border border-white/10 rounded bg-white/5 backdrop-blur-sm mb-8">
                 <p className="text-lg leading-relaxed">
                   {lang === 'en' 
                     ? "Our inner system is working perfectly for the goals it currently has. If we want different results, we need to optimize the system for a different goal. We often think the system is broken and try to change it with pressure."
                     : "سیستم درونی ما داره به شکل بی‌نقصی برای اهداف فعلیش کار می‌کنه. اگه نتایج متفاوتی می‌خوایم، لازمه که اهدافِ سیستم رو بازتعریف و بهینه کنیم. اغلب فکر می‌کنیم که این سیستم مشکل داره و باید با زور تغییرش بدیم."}
                 </p>
               </div>
               <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => setView('approach')}
                  className={`inline-flex items-center gap-3 ${gradientTextClasses} font-black uppercase text-sm tracking-widest hover:opacity-80 transition-opacity bg-white px-6 py-3 rounded-full`}
                >
                  {lang === 'en' ? "Explore my Methodology" : "کاوش در رویکرد من"}
                  <ArrowRight className="w-5 h-5 text-teal-500" />
                </button>
               </div>
             </div>
          </div>
        </section>

        {/* 6. Who is it NOT for */}
        <section className="mb-20">
          <SectionHeader title={lang === 'en' ? "Who is this Coaching NOT For?" : "این کوچینگ برای چه کسانی نیست؟"} />
          <div className="grid md:grid-cols-2 gap-4">
             {[
               { en: "Those looking for ready-made prescriptions and quick answers", fa: "کسایی که دنبال نسخه‌ی آماده و پاسخ سریع هستن" },
               { en: "Those unwilling to face their inner self", fa: "کسایی که حاضر به مواجهه‌ی درونی نیستن" },
               { en: "Those not taking responsibility for change", fa: "کسایی که مسئولیت تغییر رو به عهده نمی‌گیرن" },
               { en: "Those not committed to attending sessions", fa: "کسایی که به حضور در جلسه‌ها متعهد نمی‌مونن" }
             ].map((item, i) => (
               <div key={i} className="flex items-center gap-3 p-4 bg-gray-50 rounded-sm border border-gray-100">
                 <AlertCircle className="w-5 h-5 text-gray-500 shrink-0" />
                 <span className="text-gray-600 font-medium">{item[lang]}</span>
               </div>
             ))}
          </div>
        </section>

        {/* 7. FAQ */}
        <section className="mb-10">
          <SectionHeader title={lang === 'en' ? "Common Questions" : "سوالات متداول"} />
          <div className="grid gap-4">
             {[
               { 
                 q: { en: "What if I don't know what I want?", fa: "اگه ندونم چی می‌خوام چی؟" }, 
                 a: { en: "That's often where we start. Clarity emerges during the work, not before it.", fa: "اغلب از همین‌جا شروع می‌کنیم. شفافیت قبل از کار نمیاد، بلکه در حین کار پیدا می‌شه." } 
               },
               { 
                 q: { en: "How long does coaching take?", fa: "کوچینگ چقدر طول می‌کشد؟" }, 
                 a: { en: "No set time. We determine a time based on the result you want and move towards it. Some see changes in months, others work for a year. ICF suggests a minimum of 3 months.", fa: "زمان مشخصی نداره. با توجه به نتیجه‌ای که می‌خوای بهش برسی با هم زمانی تعیین می‌کنیم و در جهتش پیش می‌ریم. بعضی‌ها در چند ماه تغییر می‌بینند، بعضی یک سال کار می‌کنند. فدراسیون کوچینگ حداقل زمان ۳ ماه رو پیشنهاد می‌ده." } 
               },
               { 
                 q: { en: "Is it going to be hard and uncomfortable?", fa: "آیا قراره سخت و ناراحت‌کننده باشه؟" }, 
                 a: { en: "Hard, yes, but not necessarily upsetting. Growth requires facing fears and leaving the comfort zone. This difficulty is rewarding.", fa: "سخت بله اما ناراحت کننده فکر نکنم. رشد نیازمندِ روبرو شدن با ترس‌هاست. نیازمند خارج شدن از منطقه‌ی امنه که می‌تونه سخت باشه. اما این سختی خوش‌آینده." } 
               },
               { 
                 q: { en: "Difference from self-help books?", fa: "چه فرقی با کتاب‌های خودیاری داره؟" }, 
                 a: { en: "Books give info; coaching is discovery. What you discover yourself is easier to apply. Plus, you have support and accountability in a 'two-person relationship'.", fa: "کتاب اطلاعات می‌ده اما با کوچینگ خودت کشف می‌کنی. برای همینه که تاثیرگذارتره چون آدم چیزی که خودش کشف می‌کنه رو بهتر و راحت‌تر در زندگیش اعمال می‌کنه تا چیزی که یه نفر دیگه بهش می‌گه. بعلاوه در کوچینگ حمایت، پاسخگویی و دیدن نقاط کور رو از سمت من داری. که این کار در «رابطه‌ی دو انسان» اتفاق می‌افته." } 
               }
             ].map((faq, i) => (
               <details key={i} className="group border border-gray-100 rounded-sm bg-white open:shadow-lg transition-all duration-300">
                 <summary className="flex cursor-pointer items-center justify-between p-6 font-bold text-brandDark">
                   {faq.q[lang]}
                   <ChevronDown className="h-5 w-5 transition-transform group-open:rotate-180 text-teal-500" />
                 </summary>
                 <div className="px-6 pb-6 pt-0 text-gray-500 leading-relaxed font-medium bg-gray-50/50">
                   {faq.a[lang]}
                 </div>
               </details>
             ))}
          </div>
        </section>

        {/* New Transition Link to Process Page */}
        <section className="mb-20">
          <button 
            onClick={() => setView('process')}
            className={`inline-flex items-center gap-2 ${gradientTextClasses} font-black uppercase tracking-widest border-b-2 border-teal-500 pb-1 hover:opacity-80 transition-opacity`}
          >
            {lang === 'en' ? "Coaching Process in Action" : "فرایند کوچینگ در عمل"}
            <ArrowRight className="w-4 h-4 text-teal-500" />
          </button>
        </section>

        {/* 8. Ready? CTA */}
        <section className="mb-20">
          <div className="bg-gray-50 p-10 md:p-16 rounded-sm text-center border border-gray-200">
             <div className="space-y-8">
               <h2 className="text-3xl md:text-5xl font-black uppercase leading-none text-brandDark">
                 {lang === 'en' ? "Ready?" : "آماده‌ای؟"}
               </h2>
               <p className="text-gray-500 max-w-xl mx-auto text-lg font-medium whitespace-pre-line">
                 {lang === 'en' 
                   ? "An honest chat. To answer your questions and explore your path.\nFree, 30-60 mins, no obligation." 
                   : "یک گپ صادقانه. برای برطرف کردن سوالات تو و کشف مسیر تو.\nرایگان، ۳۰-۶۰ دقیقه، بدون تعهد"}
               </p>
               <div className="flex flex-col md:flex-row gap-4 justify-center pt-4">
                 <a 
                   href="https://calendar.app.google/NbuyxdkKnzVU9gj17" 
                   target="_blank"
                   rel="noopener noreferrer"
                   className={`inline-flex items-center justify-center gap-3 ${gradientBgClasses} text-white hover:opacity-90 px-8 py-4 text-lg font-black uppercase tracking-widest transition-all shadow-xl hover:shadow-2xl rounded-full`}
                 >
                   <Calendar className="w-5 h-5" />
                   {lang === 'en' ? "Request Discovery Session" : "درخواست جلسه‌ی اکتشافی"}
                 </a>
               </div>
             </div>
          </div>
        </section>

      </div>
    </div>
  );
};

export default ImpactPage;
